/**
 * Demo data fixtures for the Welcome Tour.
 *
 * All data is based on real tenant snapshots (DMX 80 U machine),
 * adapted with demo IDs (999990xxx) and relative timestamps.
 *
 * Timestamps use offsets from `Date.now()` so the timeline always
 * looks "fresh" regardless of when the tour runs.
 */

import {
  DEMO_WELCOME_MACHINE_ID,
  DEMO_BATCH_ID,
  DEMO_NC_FILE_ID,
  DEMO_PART_ID,
  DEMO_OPERATION_ID,
  DEMO_PRODUCTION_ORDER_ID,
} from '../constants';

// --- Timestamp helpers ---

const minutesAgo = (mins: number): string =>
  new Date(Date.now() - mins * 60 * 1000).toISOString();

const minutesFromStart = (startMinsAgo: number, offsetMins: number): string =>
  new Date(Date.now() - (startMinsAgo - offsetMins) * 60 * 1000).toISOString();

// The batch spans ~55 minutes, started ~55 minutes ago
const BATCH_START_MINS_AGO = 55;

// --- Demo Latest Machine Event (for MachineActivityBlock) ---

export const createDemoLatestMachineEvent = () => ({
  createdBy: 'Demo System',
  createdOn: minutesAgo(8),
  endedOn: null,
  executionMode: 'Automatic',
  machineId: DEMO_WELCOME_MACHINE_ID,
  modifiedBy: 'Demo System',
  modifiedOn: minutesAgo(1),
  ncFileActive: 'M140-71V1101A',
  ncFileIdActive: null,
  ncFileIdMain: null,
  ncFileIdSelected: null,
  ncFileMain: 'M140-71V1101A',
  ncFileSelected: 'M140-71V1101A',
  ncFileStatusActive: null,
  ncFileStatusMain: null,
  ncFileStatusSelected: null,
  ncFileVersionFileContentHashActive: null,
  ncFileVersionFileContentHashMain: null,
  ncFileVersionFileContentHashSelected: null,
  ncFileVersionIdActive: 1,
  ncFileVersionIdMain: null,
  ncFileVersionIdSelected: 1,
  ncFileVersionSequenceNumberActive: null,
  ncFileVersionSequenceNumberMain: null,
  ncFileVersionSequenceNumberSelected: null,
  ncSmartLinkIdActive: null,
  ncSmartLinkIdMain: null,
  ncSmartLinkIdSelected: null,
  partitionKey: DEMO_WELCOME_MACHINE_ID,
  quantityDelta: 1,
  sequenceNumber: 32748,
  startedOn: minutesAgo(8),
  status: 'Active',
  subStatus: 'Running',
});

// --- Demo Machine ---

export const createDemoMachine = () => ({
  machineId: DEMO_WELCOME_MACHINE_ID,
  id: DEMO_WELCOME_MACHINE_ID,
  shortName: 'DMX 80 U',
  fullName: 'DMX 80 U',
  machineStatus: {
    status: 'Active',
    subStatus: 'Running',
    executionMode: 'Automatic',
    ncFileStatus: 'Change',
    isConnected: true,
    lastUpdatedOn: minutesAgo(1),
    targetDuration: 600,
    productionBatchId: DEMO_BATCH_ID,
    ncFileId: DEMO_NC_FILE_ID,
    ncFileContentHash: 'demo-nc-file-hash-001',
    ncFileTransferFromMachineState: 'Success',
    documentId: null,
    imageHash: null,
    ncFile: 'M140-71V1101A',
    partDetails: {
      partId: DEMO_PART_ID,
      partName: '19683',
      description: 'Glue tool NXT3 Mounting block',
      drawing: '4722-074-35921',
      position: '20150129',
      revision: '',
    },
    productionOrderDetails: null,
    expectedEndOn: new Date(Date.now() + 3600000).toISOString(),
  },
  latestMachineEvent: createDemoLatestMachineEvent(),
  machineConnection: {
    switchedOn: true,
  },
  machineGroups: [],
  operations: [],
});

// --- Demo Batch Statistics (from productionBatchStatistics API) ---

export const createDemoBatchStatistics = () => ({
  productionBatchId: DEMO_BATCH_ID,
  batchNumber: '26-005315',
  batchSequenceNumber: 5315,

  // Status
  isInProgress: true,
  isClosed: false,

  // Quantity
  quantityReady: 4,

  // Machine / NC file
  machineId: DEMO_WELCOME_MACHINE_ID,
  machineShortName: 'DMX 80 U',
  machineFullName: 'DMX 80 U',
  ncFileId: DEMO_NC_FILE_ID,
  ncFileName: 'M140-71V1101A',
  ncFileStatus: 'Change',

  // Links (PartialLink — "Batch not linked to Job")
  links: [],
  linkStatus: 'PartialLink',

  // Timestamps
  startedOn: minutesAgo(BATCH_START_MINS_AGO),
  endedOn: minutesAgo(9),
  durationInSeconds: 2823,

  // Actual times (from real data)
  actualActiveTimeAverageInSeconds: 529,
  actualActiveTimeTotalInSeconds: 2117,
  actualChangeTimeAverageInSeconds: 58,
  actualChangeTimeTotalInSeconds: 232,
  actualCycleTimeAverageInSeconds: 587,
  actualCycleTimeTotalInSeconds: 2349,
  actualSetupTimeInSeconds: 877,
  actualTotalTimeTotalInSeconds: 3226,

  // Ideal times
  idealActiveTimeAverageInSeconds: 20,
  idealActiveTimeSetManually: false,
  idealActiveTimeTotalInSeconds: 80,
  idealChangeTimeAverageInSeconds: 3,
  idealChangeTimeSetManually: false,
  idealChangeTimeTotalInSeconds: 12,
  idealCycleTimeAverageInSeconds: 23,
  idealCycleTimeSetManually: false,
  idealCycleTimeTotalInSeconds: 92,
  idealSetupTimeInSeconds: 99,
  idealSetupTimeSetManually: false,
  idealTargetActiveTimeTotalInSeconds: 0,
  idealTargetChangeTimeTotalInSeconds: 0,
  idealTargetCycleTimeTotalInSeconds: 0,
  idealTargetSetupTimeInSeconds: 99,
  idealTargetTotalTimeTotalInSeconds: 0,
  idealTotalTimeTotalInSeconds: 191,

  // Paid times (all zero in this snapshot)
  paidActiveTimeAverageInSeconds: 0,
  paidActiveTimeTotalInSeconds: 0,
  paidChangeTimeAverageInSeconds: 0,
  paidChangeTimeTotalInSeconds: 0,
  paidCycleTimeAverageInSeconds: 0,
  paidCycleTimeTotalInSeconds: 0,
  paidSetupTimeInSeconds: 0,
  paidTargetActiveTimeTotalInSeconds: 0,
  paidTargetChangeTimeTotalInSeconds: 0,
  paidTargetCycleTimeTotalInSeconds: 0,
  paidTargetSetupTimeInSeconds: 0,
  paidTargetTotalTimeTotalInSeconds: 0,
  paidTotalTimeTotalInSeconds: 0,

  // Scheduled times
  scheduledActiveTimeAverageInSeconds: 56,
  scheduledActiveTimeSetManually: false,
  scheduledActiveTimeTotalInSeconds: 224,
  scheduledChangeTimeAverageInSeconds: 8,
  scheduledChangeTimeSetManually: false,
  scheduledChangeTimeTotalInSeconds: 32,
  scheduledCycleTimeAverageInSeconds: 64,
  scheduledCycleTimeTotalInSeconds: 256,
  scheduledSetupTimeInSeconds: 877,
  scheduledSetupTimeSetManually: false,
  scheduledTargetActiveTimeTotalInSeconds: 0,
  scheduledTargetChangeTimeTotalInSeconds: 0,
  scheduledTargetCycleTimeTotalInSeconds: 0,
  scheduledTargetSetupTimeInSeconds: 992,
  scheduledTargetTotalTimeTotalInSeconds: 0,
  scheduledTotalTimeTotalInSeconds: (BATCH_START_MINS_AGO + 10) * 60, // Expected end ≈ 10 min from now

  // Percentile data
  maxActiveTimePerCycleInSeconds: 621,
  maxChangeTimePerCycleInSeconds: 88,
  maxCycleTimePerCycleInSeconds: 709,
  p25ActiveTimeInSeconds: 483,
  p25ChangeTimeInSeconds: 72,
  p50ActiveTimeInSeconds: 546,
  p50ChangeTimeInSeconds: 73,
  p75ActiveTimeInSeconds: 592,
  p75ChangeTimeInSeconds: 80,

  // Metadata
  createdBy: 'Demo System',
  createdOn: minutesAgo(BATCH_START_MINS_AGO),
  modifiedBy: 'Demo System',
  modifiedOn: minutesAgo(1),
});

// --- Demo Timeline Events (from eventsTimelineDetails API) ---

export const createDemoTimelineEvents = () => {
  // 11 events spanning ~55 minutes, same pattern as real data:
  // Setup(MDI) → Setup(Manual) → Active → Change → Active → Change → Setup(Interrupted) → Setup(RPF) → Active → Change → Active
  const s = BATCH_START_MINS_AGO; // start offset

  return [
    {
      executionMode: 'MDI',
      ncFileId: DEMO_NC_FILE_ID,
      ncFileMain: 'M140-71V1101A',
      ncFileStatus: 'Unknown',
      ncFileVersionSequenceNumber: 1,
      productionBatchId: DEMO_BATCH_ID,
      quantityDelta: 0,
      subStatus: 'Idle',
      sequenceNumberEndedOn: 32738,
      sequenceNumberStartedOn: 32738,
      startedOn: minutesFromStart(s, 0),          // 0:00
      endedOn: minutesFromStart(s, 1.1),           // 1:06
      status: 'Setup',
    },
    {
      executionMode: 'Manual',
      ncFileId: DEMO_NC_FILE_ID,
      ncFileMain: 'M140-71V1101A',
      ncFileStatus: 'Unknown',
      ncFileVersionSequenceNumber: 1,
      productionBatchId: DEMO_BATCH_ID,
      quantityDelta: 0,
      subStatus: 'Running',
      sequenceNumberEndedOn: 32739,
      sequenceNumberStartedOn: 32739,
      startedOn: minutesFromStart(s, 1.1),         // 1:06
      endedOn: minutesFromStart(s, 11.2),          // 11:12
      status: 'Setup',
    },
    {
      executionMode: 'Automatic',
      ncFileId: DEMO_NC_FILE_ID,
      ncFileMain: 'M140-71V1101A',
      ncFileStatus: 'Unknown',
      ncFileVersionSequenceNumber: 1,
      productionBatchId: DEMO_BATCH_ID,
      quantityDelta: 1,
      subStatus: 'Running',
      sequenceNumberEndedOn: 32740,
      sequenceNumberStartedOn: 32740,
      startedOn: minutesFromStart(s, 11.2),        // 11:12
      endedOn: minutesFromStart(s, 21.5),          // 21:30
      status: 'Active',
    },
    {
      executionMode: 'Automatic',
      ncFileId: DEMO_NC_FILE_ID,
      ncFileMain: 'M140-71V1101A',
      ncFileStatus: 'Unknown',
      ncFileVersionSequenceNumber: 1,
      productionBatchId: DEMO_BATCH_ID,
      quantityDelta: 0,
      subStatus: 'Idle',
      sequenceNumberEndedOn: 32741,
      sequenceNumberStartedOn: 32741,
      startedOn: minutesFromStart(s, 21.5),        // 21:30
      endedOn: minutesFromStart(s, 23),            // 23:00
      status: 'Change',
    },
    {
      executionMode: 'Automatic',
      ncFileId: DEMO_NC_FILE_ID,
      ncFileMain: 'M140-71V1101A',
      ncFileStatus: 'Unknown',
      ncFileVersionSequenceNumber: 1,
      productionBatchId: DEMO_BATCH_ID,
      quantityDelta: 1,
      subStatus: 'Running',
      sequenceNumberEndedOn: 32742,
      sequenceNumberStartedOn: 32742,
      startedOn: minutesFromStart(s, 23),          // 23:00
      endedOn: minutesFromStart(s, 32.7),          // 32:42
      status: 'Active',
    },
    {
      executionMode: 'Automatic',
      ncFileId: DEMO_NC_FILE_ID,
      ncFileMain: 'M140-71V1101A',
      ncFileStatus: 'Unknown',
      ncFileVersionSequenceNumber: 1,
      productionBatchId: DEMO_BATCH_ID,
      quantityDelta: 0,
      subStatus: 'Idle',
      sequenceNumberEndedOn: 32743,
      sequenceNumberStartedOn: 32743,
      startedOn: minutesFromStart(s, 32.7),        // 32:42
      endedOn: minutesFromStart(s, 33.9),          // 33:54
      status: 'Change',
    },
    {
      executionMode: 'Other',
      ncFileId: DEMO_NC_FILE_ID,
      ncFileMain: 'M140-71V1101A',
      ncFileStatus: 'Unknown',
      ncFileVersionSequenceNumber: 1,
      productionBatchId: DEMO_BATCH_ID,
      quantityDelta: 0,
      subStatus: 'Interrupted',
      sequenceNumberEndedOn: 32744,
      sequenceNumberStartedOn: 32744,
      startedOn: minutesFromStart(s, 33.9),        // 33:54
      endedOn: minutesFromStart(s, 36.2),          // 36:12
      status: 'Setup',
    },
    {
      executionMode: 'RPF',
      ncFileId: DEMO_NC_FILE_ID,
      ncFileMain: 'M140-71V1101A',
      ncFileStatus: 'Unknown',
      ncFileVersionSequenceNumber: 1,
      productionBatchId: DEMO_BATCH_ID,
      quantityDelta: 0,
      subStatus: 'Idle',
      sequenceNumberEndedOn: 32745,
      sequenceNumberStartedOn: 32745,
      startedOn: minutesFromStart(s, 36.2),        // 36:12
      endedOn: minutesFromStart(s, 37.4),          // 37:24
      status: 'Setup',
    },
    {
      executionMode: 'Automatic',
      ncFileId: DEMO_NC_FILE_ID,
      ncFileMain: 'M140-71V1101A',
      ncFileStatus: 'Unknown',
      ncFileVersionSequenceNumber: 1,
      productionBatchId: DEMO_BATCH_ID,
      quantityDelta: 1,
      subStatus: 'Running',
      sequenceNumberEndedOn: 32746,
      sequenceNumberStartedOn: 32746,
      startedOn: minutesFromStart(s, 37.4),        // 37:24
      endedOn: minutesFromStart(s, 45.9),          // 45:54
      status: 'Active',
    },
    {
      executionMode: 'Automatic',
      ncFileId: DEMO_NC_FILE_ID,
      ncFileMain: 'M140-71V1101A',
      ncFileStatus: 'Unknown',
      ncFileVersionSequenceNumber: 1,
      productionBatchId: DEMO_BATCH_ID,
      quantityDelta: 0,
      subStatus: 'Idle',
      sequenceNumberEndedOn: 32747,
      sequenceNumberStartedOn: 32747,
      startedOn: minutesFromStart(s, 45.9),        // 45:54
      endedOn: minutesFromStart(s, 47.1),          // 47:06
      status: 'Change',
    },
    {
      executionMode: 'Automatic',
      ncFileId: DEMO_NC_FILE_ID,
      ncFileMain: 'M140-71V1101A',
      ncFileStatus: 'Unknown',
      ncFileVersionSequenceNumber: 1,
      productionBatchId: DEMO_BATCH_ID,
      quantityDelta: 1,
      subStatus: 'Running',
      sequenceNumberEndedOn: 32748,
      sequenceNumberStartedOn: 32748,
      startedOn: minutesFromStart(s, 47.1),        // 47:06
      endedOn: minutesFromStart(s, 55),            // 55:00 (current)
      status: 'Active',
    },
  ];
};

// --- Demo Quantity Events (from productionBatchesSearch for count diamonds) ---

export const createDemoQuantityEvents = () => {
  const s = BATCH_START_MINS_AGO;

  return [
    {
      startedOn: minutesFromStart(s, 11.2),
      endedOn: minutesFromStart(s, 21.5),
      quantityDelta: 1,
    },
    {
      startedOn: minutesFromStart(s, 23),
      endedOn: minutesFromStart(s, 32.7),
      quantityDelta: 1,
    },
    {
      startedOn: minutesFromStart(s, 37.4),
      endedOn: minutesFromStart(s, 45.9),
      quantityDelta: 1,
    },
    {
      startedOn: minutesFromStart(s, 47.1),
      endedOn: minutesFromStart(s, 47.2),
      quantityDelta: 1,
    },
  ];
};

// --- Demo Batch Search Result (wraps quantityEvents for productionBatchesSearch) ---

export const createDemoBatchSearchResult = () => ({
  searchResult: [
    {
      batchSequenceNumber: 0,
      durationInSeconds: 0,
      endedOn: '0001-01-01T00:00:00.000Z',
      idealActiveTimeSetManually: false,
      idealChangeTimeSetManually: false,
      idealCycleTimeSetManually: false,
      idealSetupTimeSetManually: false,
      isClosed: false,
      isInProgress: false,
      machineId: 0,
      ncFileId: 0,
      productionBatchId: 0,
      productionBatchKey: '0_0',
      quantity: 0,
      quantityEvents: createDemoQuantityEvents(),
      startedOn: '0001-01-01T00:00:00.000Z',
      tenantId: 0,
      score: 1,
    },
  ],
  facets: {
    LinkStatus: [{ value: 'PartialLink', count: 1 }],
    NcFileStatus: [{ value: 'Linked', count: 1 }],
  },
  totalResultCount: 1,
});

// --- Demo Machine Statistics (from machineStatistics API — Performance page) ---
// NOTE: totalActiveSecs, totalProductiveSecs, totalActiveSecsPrevious, totalProductiveSecsPrevious
// are placeholder values here. The inject function (performance.ts) overrides them with values
// computed from the hourly chart data so sidebar always matches the chart header.

export const createDemoMachineStatistics = () => [
  {
    machineId: DEMO_WELCOME_MACHINE_ID,
    shortName: 'DMX 80 U',
    fullName: 'DMX 80 U',
    totalActiveSecs: 0,
    totalProductiveSecs: 0,
    totalChangeoverSecs: 0,
    totalNotProductiveSecs: 65438,
    totalSetupSecs: 0,
    totalConnectedSecs: 85421,
    totalDisconnectedSecs: 979,
    totalActiveCycles: 24,
    totalActiveSecsPrevious: 0,
    totalProductiveSecsPrevious: 0,
  },
];

// --- Demo NC File Statistics (from ncFileStatistics API — Performance page) ---
// NOTE: totalActiveSecs, totalProductiveSecs etc. are placeholder values.
// The inject function (performance.ts) overrides them with values computed from
// the hourly chart data so NC file breakdown sums match the chart header total.

// Proportions for splitting totals between NC files (must sum to 1.0)
export const NC_FILE_SPLIT = { first: 0.60, second: 0.40 };
// Previous period uses slightly different proportions for realism
export const NC_FILE_SPLIT_PREV = { first: 0.55, second: 0.45 };

export const createDemoNcFileStatistics = () => [
  {
    ncFileId: DEMO_NC_FILE_ID,
    ncFileName: 'M140-71V1101A',
    totalActiveSecs: 0,
    totalProductiveSecs: 0,
    totalChangeoverSecs: 0,
    totalSetupSecs: 0,
    totalNotProductiveSecs: 32000,
    totalActiveCycles: 14,
    totalActiveSecsPrevious: 0,
    totalProductiveSecsPrevious: 0,
  },
  {
    ncFileId: DEMO_NC_FILE_ID + 1,
    ncFileName: 'P250-42R0803B',
    totalActiveSecs: 0,
    totalProductiveSecs: 0,
    totalChangeoverSecs: 0,
    totalSetupSecs: 0,
    totalNotProductiveSecs: 33438,
    totalActiveCycles: 10,
    totalActiveSecsPrevious: 0,
    totalProductiveSecsPrevious: 0,
  },
];

// --- Demo Machine Stats Hour (from MachineStatsHour API — Performance bar chart) ---
// Fields expected by getValuesBar(): totalActiveSecs, totalSetupSecs,
// totalChangeSecs, date/dateTime. Backend returns factory-local timestamps (no Z suffix).

const padTwo = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

const localHourTimestamp = (hour: number): string => {
  const now = new Date();
  return `${now.getFullYear()}-${padTwo(now.getMonth() + 1)}-${padTwo(now.getDate())}T${padTwo(hour)}:00:00`;
};

export const createDemoMachineStatsHour = () => {
  const currentHour = new Date().getHours();
  const startHour = 6;
  const entries: any[] = [];

  for (let h = startHour; h <= currentHour; h++) {
    const date = localHourTimestamp(h);

    if (h < 8) {
      // Early hours — mostly not productive
      entries.push({
        date,
        totalActiveSecs: 120,
        totalSetupSecs: 60,
        totalChangeSecs: 0,
      });
    } else if (h === currentHour) {
      // Current hour — partial
      const fraction = new Date().getMinutes() / 60;
      entries.push({
        date,
        totalActiveSecs: Math.floor(1800 * fraction),
        totalSetupSecs: Math.floor(200 * fraction),
        totalChangeSecs: Math.floor(120 * fraction),
      });
    } else {
      // Normal working hours — deterministic values (no Math.random to keep chart stable)
      const seed = h * 137;
      entries.push({
        date,
        totalActiveSecs: 1600 + (seed % 400),
        totalSetupSecs: 180 + (seed % 120),
        totalChangeSecs: 80 + (seed % 60),
      });
    }
  }

  return entries;
};

// --- Demo Machine Stats Hour — Previous Period (same weekday, 1 week ago — full 24h day) ---
// Used for sidebar gray bar and line chart previous period comparison.

const localHourTimestampDaysAgo = (hour: number, daysAgo: number): string => {
  const now = new Date();
  const target = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
  return `${target.getFullYear()}-${padTwo(target.getMonth() + 1)}-${padTwo(target.getDate())}T${padTwo(hour)}:00:00`;
};

export const createDemoPreviousMachineStatsHour = () => {
  const entries: any[] = [];

  for (let h = 0; h < 24; h++) {
    const date = localHourTimestampDaysAgo(h, 7);

    if (h < 6 || h >= 22) {
      // Night hours — no activity
      entries.push({
        date,
        totalActiveSecs: 0,
        totalSetupSecs: 0,
        totalChangeSecs: 0,
      });
    } else if (h < 8) {
      // Early morning — light activity
      entries.push({
        date,
        totalActiveSecs: 180,
        totalSetupSecs: 90,
        totalChangeSecs: 30,
      });
    } else {
      // Full working hours — deterministic values (seed = h * 163, different from current period)
      const seed = h * 163;
      entries.push({
        date,
        totalActiveSecs: 1700 + (seed % 350),
        totalSetupSecs: 200 + (seed % 100),
        totalChangeSecs: 90 + (seed % 50),
      });
    }
  }

  return entries;
};

// --- Demo Scheduled Operation (for OP Scheduled Jobs screen) ---

export const createDemoScheduledOperation = () => ({
  productionOperationId: DEMO_OPERATION_ID,
  productionOperationNumber: '1162-1-25',
  productionOrderId: DEMO_PRODUCTION_ORDER_ID,
  productionOrderNumber: '1162',
  operationId: DEMO_OPERATION_ID,
  operationNumber: '25',
  operationName: 'Frezen',
  description: 'Kupplungshalter FW',
  status: 'NotStarted',
  isActive: false,
  scheduledStartDate: new Date(Date.now()).toISOString(),
  scheduledEndDate: new Date(Date.now() + 8 * 3600000).toISOString(),
  scheduleExceedsOn: new Date(Date.now() + 7 * 86400000).toISOString(),
  quantityTarget: 30,
  quantityProduced: 0,
  partNumber: 'KH-FW-001',
  partDescription: 'Kupplungshalter FW',
  customerName: 'AMB GmbH',
  machines: [
    { machineId: DEMO_WELCOME_MACHINE_ID, machineName: 'DMX 80 U', linkType: 'Preferred' },
  ],
  linkedNcFiles: [
    {
      ncFileId: DEMO_NC_FILE_ID,
      ncFileName: 'M140-71V1101A',
      ncFileStatus: 'Linked',
      runTimePerPcsSecond: 529,
      setupTimeSecond: 877,
      changeTimePerPcsSecond: 58,
      createdOn: minutesAgo(60),
      linkedBy: 'Demo System',
      linkedOn: minutesAgo(60),
      memo: null,
      ncFileStatusSetBy: null,
      ncFileStatusSetOn: null,
      setupSheet: null,
      productionBatchId: 0,
      productionBatchNumber: null,
      actualTotalTimeInSeconds: null,
      quantityProduced: null,
    },
    {
      ncFileId: DEMO_NC_FILE_ID + 1,
      ncFileName: 'P250-42R0803B',
      ncFileStatus: 'Linked',
      runTimePerPcsSecond: 340,
      setupTimeSecond: 450,
      changeTimePerPcsSecond: 42,
      createdOn: minutesAgo(90),
      linkedBy: 'Demo System',
      linkedOn: minutesAgo(90),
      memo: null,
      ncFileStatusSetBy: null,
      ncFileStatusSetOn: null,
      setupSheet: null,
      productionBatchId: 0,
      productionBatchNumber: null,
      actualTotalTimeInSeconds: null,
      quantityProduced: null,
    },
    {
      ncFileId: DEMO_NC_FILE_ID + 2,
      ncFileName: 'K080-15F0204C',
      ncFileStatus: 'Linked',
      runTimePerPcsSecond: 180,
      setupTimeSecond: 300,
      changeTimePerPcsSecond: 25,
      createdOn: minutesAgo(120),
      linkedBy: 'Demo System',
      linkedOn: minutesAgo(120),
      memo: null,
      ncFileStatusSetBy: null,
      ncFileStatusSetOn: null,
      setupSheet: null,
      productionBatchId: 0,
      productionBatchNumber: null,
      actualTotalTimeInSeconds: null,
      quantityProduced: null,
    },
  ],
  totalTimeScheduledSeconds: 81600,
  totalTimeActualSeconds: 0,
  operationTimeSeconds: 81600,
  elapsedTimeSeconds: 0,
  setupTimeSeconds: 877,
  actualStartDate: null,
  actualEndDate: null,
  productionStartedOn: null,
  productionEndedOn: null,
  productionBatchId: 0,
  productionBatchNumber: '',
  productionBatchStatus: null,
  percentageComplete: 0,
  imageDocumentId: null,
  imageHash: null,
  imageData: null,
  operationImageData: null,
  operationImageDocumentId: null,
  operationImageHash: null,
  partId: DEMO_PART_ID,
  partImageData: null,
  partImageDocumentId: null,
  partImageHash: null,
  partDrawingNumber: null,
  partDrawingPosition: null,
  partDrawingRevision: null,
  productionOrderImageData: null,
  productionOrderImageDocumentId: null,
  productionOrderImageHash: null,
  shortDescription: null,
  sortOrder: 1,
  sourceSystemStatus: 0,
  score: 1,
  createdBy: 'Demo System',
  createdOn: minutesAgo(120),
  modifiedBy: 'Demo System',
  modifiedOn: minutesAgo(1),
  workplaceMachineId: null,
  workplaceMachineName: null,
  workplaceMemo: null,
  workplaceName: null,
  workplaceNumber: null,
  workplaceType: null,
  outsourced: false,
  expectedStartOn: null,
  expectedEndOn: null,
  hasPriorityOverride: false,
  scheduledStartDateOverride: null,
  scheduledOnMachineId: null,
  scheduledOnMachineName: null,
  finishedOn: null,
  finishedBy: null,
});

// --- Demo OEE/TEEP Data (for OeeTodayBlock on Live Overview) ---

export const createDemoOeeTeepData = () => ({
  machineId: DEMO_WELCOME_MACHINE_ID,
  shortName: 'DMX 80 U',
  fullName: 'DMX 80 U',
  totalDurationSecs: 86400,
  totalNotScheduledSecs: 28800,
  totalUnScheduledSecs: 3600,
  totalScheduledSecs: 54000,
  totalDisturbancesSecs: 2700,
  totalSetupLossSecs: 1200,
  totalSlowSetupLossSecs: 600,
  totalChangeLossSecs: 900,
  totalSlowChangeLossSecs: 300,
  totalActiveSecs: 49200,
  totalSpeedLossSecs: 4920,
  totalPerformanceSecs: 44280,
  totalQualityLossSecs: 3100,
  totalQualitySecs: 41180,
  oee: 0.75,
  teep: 0.61,
});

// --- Demo Tools Data (for ToolsBlock on Live Overview) ---

export const createDemoToolsData = () => [
  {
    toolId: 999990201,
    toolNumber: 'T1',
    toolName: 'FRAESER D16 R0.8',
    toolDescription: 'Milling cutter D16 corner radius 0.8',
    toolType: 'Milling',
    ncFileId: DEMO_NC_FILE_ID,
    ncFileName: 'M140-71V1101A',
  },
];

// --- Demo NC File By ID (for TopBlock first-ran info) ---

export const createDemoNCFileById = () => ({
  ncFileId: DEMO_NC_FILE_ID,
  FirstRanOn: '2025-07-08T06:23:00Z',
  FirstRanByMachineName: 'DMX 80 U',
});

// --- Demo Connector (for Operator Panel) ---

export const createDemoConnector = () => ({
  status: [
    {
      workerType: 'Machines',
      status: 'Online',
      lastAliveEventReceivedOn: minutesAgo(1),
    },
  ],
  connectorId: 999990200,
  isPRODUCTIONExist: false,
});

// --- Demo NC File Content (G-code) ---

export const createDemoNcFileContent = (): string => `%
<M140-71V2003A>
(PROGRAMMA NR:-71V1003A--)
(TEK.NR:372323------------)
(MATERIAAL:ALU------------)
(DATUM:22-01-2025----------)
N1 G90 G17 G40 G80 G49
N2 T1 M6 (FRAESER D16 R0.8)
N3 G0 G54 X0 Y0 S8000 M3
N4 G43 H1 Z50. M8
N5 G0 Z5.
N6 G1 Z-2. F1500
N7 G41 D1 X10. Y10.
N8 G1 X80. F2000
N9 G2 X90. Y20. R10.
N10 G1 Y60.
N11 G2 X80. Y70. R10.
N12 G1 X20.
N13 G2 X10. Y60. R10.
N14 G1 Y20.
N15 G2 X20. Y10. R10.
N16 G1 X80.
N17 G40 X90. Y0.
N18 G0 Z50. M9
N19 M5
N20 M30
%`;
